import { useEffect, useRef, useState, useMemo } from 'react'
import styled from '@emotion/styled'
import { useIsPortrait } from '../../../util/breakpoints'
import { isInScrollRange } from '../../../util/scrollPercentage'
import { AnimationItem } from 'lottie-web'
import '../../../lib/lottie_light.min.js'
import { getFirstFrameUrl, getLastFrameUrl } from '../../../util/path'
import { scrollstoryBackgroundColor } from '../../atoms/Atoms.js'
import { VIEWPORT_HEIGHT } from '../../../util/viewportHeight.ts'

const getCurrentPosterUrl = (lottieUrl: string, props: LottieProps) => {
  if (props.playback === 'scroll' && props.scrollPercentage > 0.5) {
    return getLastFrameUrl(lottieUrl)
  }
  return getFirstFrameUrl(lottieUrl)
}

export interface LottieProps {
  playback: 'scroll' | 'loop' | 'play_once'
  lottieLandscape: string
  lottiePortrait: string
  scrollPercentage: number
  shouldLoadAnimation: boolean
}

export function Lottie(props: LottieProps) {
  const isPortrait = useIsPortrait()
  const currentOrientationRef = useRef<'portrait' | 'landscape'>(isPortrait ? 'portrait' : 'landscape')

  const [animation, setAnimation] = useState<AnimationItem>()
  const [animationLoaded, setAnimationLoaded] = useState(false)
  const [animationPlaying, setAnimationPlaying] = useState(false)

  const lottieDivRef = useRef<HTMLDivElement>(null)

  // Only recalculate lottieUrl if the URLs are different
  const lottieUrl = useMemo(() => {
    if (props.lottiePortrait === props.lottieLandscape) {
      return props.lottieLandscape
    }
    return isPortrait && props.lottiePortrait
      ? props.lottiePortrait
      : props.lottieLandscape
  }, [isPortrait, props.lottiePortrait, props.lottieLandscape])

  // fetch & process lottie animation Data and load animation
  useEffect(() => {
    // Only update orientation if URLs are different
    if (props.lottiePortrait !== props.lottieLandscape) {
      currentOrientationRef.current = isPortrait ? 'portrait' : 'landscape'
    }

    // Cleanup function to destroy animation
    const cleanupAnimation = () => {
      if (animation) {
        setAnimationPlaying(false)
        setAnimationLoaded(false)
        animation.destroy()
        setAnimation(undefined)
      }
    }

    // If we shouldn't load animation or don't have a URL, just cleanup
    if (!props.shouldLoadAnimation || !lottieUrl || !lottieDivRef?.current) {
      cleanupAnimation()
      return
    }

    // Store the current orientation when starting the fetch
    const orientationAtFetch = currentOrientationRef.current

    fetch(lottieUrl)
      .then((response) => response.json())
      .then((data) => {
        // Only check orientation if URLs are different
        if (props.lottiePortrait !== props.lottieLandscape &&
            orientationAtFetch !== currentOrientationRef.current) {
          return // Skip this animation if orientation changed
        }

        const containsImages = data.assets?.some(
          (asset: { u: string }) => 'u' in asset
        )

        if (data.assets) {
          const pathToLottieJson = lottieUrl.slice(
            0,
            lottieUrl.lastIndexOf('/')
          )

          data.assets = data.assets.map(
            (asset: { u: string }): { u: string } => {
              if (asset.u) {
                asset.u = `${pathToLottieJson}/${asset.u}`
              }
              return asset
            }
          )
        }

        // Cleanup any existing animation before creating new one
        cleanupAnimation()

        // Load animation if we have the container and data
        if (lottieDivRef.current) {
          const newAnimation = window.lottie.loadAnimation({
            animationData: data,
            container: lottieDivRef.current,
            renderer: 'svg',
            rendererSettings: {
              preserveAspectRatio: 'xMidYMid slice',
            },
            loop: props.playback === 'loop',
            autoplay: false,
          })

          // Handle image loading
          if (containsImages) {
            newAnimation.addEventListener('loaded_images', () => {
              // Finally check if correct lottie is loaded before setting loaded state
              if (props.lottiePortrait === props.lottieLandscape ||
                  orientationAtFetch === currentOrientationRef.current) {
                setAnimationLoaded(true)
              }
            })
          } else {
            setAnimationLoaded(true)
          }

          setAnimation(newAnimation)
        }
      })
      .catch((error) => {
        console.error(`Could not fetch lottie '${lottieUrl}'`, error)
      })

    // Cleanup on unmount or when dependencies change
    return cleanupAnimation

    // we explicitly don't want to have animation in dependency array, because it would be destroyed immediately after creation
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lottieUrl, props.shouldLoadAnimation, props.playback, lottieDivRef, isPortrait])

  // Update animation if loop | play_once
  useEffect(() => {
    if (
      animation &&
      (props.playback === 'loop' || props.playback === 'play_once')
    ) {
      if (isInScrollRange(props.scrollPercentage) && !animationPlaying) {
        animation.goToAndPlay(0, true)
        setAnimationPlaying(true)
      }
    }
  }, [animation, animationPlaying, props.playback, props.scrollPercentage])

  // Update animation if scroll
  useEffect(() => {
    if (animation && animationLoaded && props.playback === 'scroll') {
      let currentFrame = 0
      if (animation.totalFrames > 0) {
        // Reachable frames go from 0 to totalFrames - 1
        currentFrame = (animation.totalFrames - 1) * props.scrollPercentage
      }

      animation.goToAndStop(currentFrame, true)
      setAnimationPlaying(true)
    }
  }, [animation, animationLoaded, props.playback, props.scrollPercentage])

  return (
    <LottieContainer>
      <Poster
        className={!animationPlaying ? 'show' : ''}
        src={getCurrentPosterUrl(lottieUrl, props)}
        alt={''}
      />
      <LottieDiv ref={lottieDivRef} />
    </LottieContainer>
  )
}

export const LottieContainer = styled.div`
  position: relative;
`

export const LottieDiv = styled.div`  
  position: relative;
  z-index: 1;
  width: 100%;
  height: ${VIEWPORT_HEIGHT};
`

export const Poster = styled.img`
  position: absolute;
  top: 0px;
  left: 0px;
  height: ${VIEWPORT_HEIGHT};
  z-index: 2;
  width: 100%;
  object-fit: cover;

  ${scrollstoryBackgroundColor}

  transition: opacity 0.3s ease-in-out;
  opacity: 0;
  &.show {
    opacity: 1;
  }
`
